import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';

import { EmailAddress } from '../../../models/email-address';

import { EmailAddressService } from '../../../services/contacts/email-address.service';
import { NotyService } from '../../../services/common/noty.service';
import { ModalService } from '../../../services/common/modal.service';
import { SpamTitanService } from '../../../services/contacts/spam-titan.service';

@Component({
  selector: 'app-email-address-index',
  templateUrl: './email-address-index.component.html',
  styleUrls: ['./email-address-index.component.scss'],
})
export class EmailAddressIndexComponent implements OnInit, OnChanges {
  /**
   * Contact id from parent component
   */
  @Input() contactId: number;

  /**
   * Indicates if contact is an intranet user
   */
  @Input() isUser: boolean = false;

  /**
   * Blacklisted emails
   * @type {Array<EmailAddress>}
   */
  blacklistEmails: Array<any> = [];

  /**
   * Selected blacklist emails for bulk delete
   */
  selectedBlacklist: Set<string> = new Set();

  /**
   * Selected whitelist emails for bulk delete
   */
  selectedWhitelist: Set<string> = new Set();

  /**
   * Indicates if a blacklisted email is being deleted
   * @type {boolean}
   */
  deletingBlacklist: boolean = false;

  /**
   * Indicates if a whitelisted email is being deleted
   * @type {boolean}
   */
  deletingWhitelist: boolean = false;

  /**
   * Email Address list
   * @type {Array<EmailAddress>}
   */
  emailAddresses: Array<EmailAddress> = [];

  /**
   * Indicates if blacklist email addresses are being loaded
   * @type {boolean}
   */
  loadingBlacklists: boolean = false;

  /**
   * Indicates if email addresses are being loaded
   * @type {boolean}
   */
  loadingEmailAddresses: boolean = false;

  /**
   * Indicates if whitelist email addresses are being loaded
   * @type {boolean}
   */
  loadingWhitelists: boolean = false;

  /**
   * New Blacklist object
   * @type {any}
   */
  newBlacklist: any = {};

  /**
   * New Whitelist object
   * @type {any}
   */
  newWhitelist: any = {};

  /**
   * Indicates if blacklist form is saving
   * @type {boolean}
   */
  savingBlacklist: boolean = false;

  /**
   * Indicates if whitelist form is saving
   * @type {boolean}
   */
  savingWhitelist: boolean = false;

  /**
   * Indicates if new blacklisted emails is to be showed
   * @type {boolean}
   */
  showBlacklists: boolean = false;

  /**
   * Indicates if new email address blacklist form is to be showed
   * @type {boolean}
   */
  showNewBlacklistForm: boolean = false;

  /**
   * Indicates if new email address form is to be showed
   * @type {boolean}
   */
  showNewEmailAddressForm: boolean = false;

  /**
   * Indicates if new email address whitelist form is to be showed
   * @type {boolean}
   */
  showNewWhitelistForm: boolean = false;

  /**
   * Indicates if new whitelisted emails is to be showed
   * @type {boolean}
   */
  showWhitelists: boolean = false;

  /**
   * Sortable options of Email Addresses sorting using Angular-sortablejs
   * @type {any}
   */
  sortableOptions: any = {
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    ghostClass: 'sortable-ghost',
    handle: '.alias-sort-handle',
    onUpdate: (event: any) => {
      this.updateEmailAddressesPosition();
    },
  };

  /**
   * Whitelisted emails
   * @type {Array<EmailAddress>}
   */
  whitelistEmails: Array<any> = [];

  constructor(private emailAddressService: EmailAddressService, private modalService: ModalService, private notyService: NotyService, private spamTitanService: SpamTitanService) {}

  // Component lifecycle methods

  /**
   * Called on Component initialization
   */
  ngOnInit() {}

  /**
   * Called when component variables change
   * @param {SimpleChanges} changes changes containing all variables changed
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes.contactId && changes.contactId.currentValue) this.loadEmailAddresses();
  }

  /**
   * Add email to blacklist
   */
  createBlacklist() {
    this.savingBlacklist = true;
    this.spamTitanService
      .createBlacklist(this.contactId, this.newBlacklist)
      .then((response_json) => {
        this.savingBlacklist = false;
        this.notyService.notify('Blacklist email saved', 'success', 5000, 'topRight');
        this.loadBlacklists();
        this.showNewBlacklistForm = false;
      })
      .catch(() => {
        this.savingBlacklist = false;
        this.notyService.notify('Error saving blacklist email', 'error', 5000, 'topRight');
      });
  }

  /**
   * Add email to whitelist
   */
  createWhitelist() {
    this.savingBlacklist = true;
    this.spamTitanService
      .createWhitelist(this.contactId, this.newWhitelist)
      .then((response_json) => {
        this.savingWhitelist = false;
        this.notyService.notify('Whitelist email saved', 'success', 5000, 'topRight');
        this.loadWhitelists();
        this.showNewWhitelistForm = false;
      })
      .catch(() => {
        this.savingWhitelist = false;
        this.notyService.notify('Error saving whitelist email', 'error', 5000, 'topRight');
      });
  }

  /**
   * Delete Blacklisted email
   * @param {EmailAddress} emailAddress Blacklisted Email address to delete
   */
  deleteBlacklistedEmail(email: string, index: number) {
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Remove Email - <strong class='text-danger'>${email}</strong> from blacklist ?</p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          this.deletingBlacklist = true;
          this.spamTitanService
            .deleteBlacklist(this.contactId, { value: email })
            .then((response_json) => {
              this.deletingBlacklist = false;
              this.notyService.notify(`Email removed ${email}`, 'warning');
              this.blacklistEmails.splice(index, 1);
            })
            .catch(() => {
              this.deletingBlacklist = false;
              this.notyService.notify('Error removing email from blacklist', 'error');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Delete Email Address
   * @param {EmailAddress} emailAddress Email address to delete
   */
  deleteEmailAddress(emailAddress: EmailAddress, index: number) {
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Delete Email Address - <strong class='text-danger'>${emailAddress.email}</strong></p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          emailAddress.deleting = true;
          this.emailAddressService
            .delete(emailAddress.id)
            .then((response_json) => {
              emailAddress.deleting = false;
              if (response_json.success) {
                this.notyService.notify(response_json.text || `Email address deleted ${emailAddress.email}`, 'warning');
                this.emailAddresses.splice(index, 1);
              } else if (response_json.warning) {
                this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
                this.modalService.confirmDialogComponent.open(`<p>${response_json.text || 'This email is used in one involvement, are you sure that this emails is to be deleted?'}</p>`).result.then(
                  (result) => {
                    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
                    if (result == 'confirmed') {
                      emailAddress.deleting = true;
                      this.emailAddressService
                        .delete(emailAddress.id, 'confirmed')
                        .then((response_json) => {
                          if (response_json.success) {
                            this.notyService.notify(response_json.text || `Email address deleted ${emailAddress.email}`, 'warning');
                            this.emailAddresses.splice(index, 1);
                          } else this.notyService.notify(response_json.text || `Error deleting email address`, 'error');
                        })
                        .catch(() => {
                          emailAddress.deleting = false;
                          this.notyService.notify('Error deleting email address', 'error');
                        });
                    }
                  },
                  (reason) => {
                    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
                  }
                );
              } else this.notyService.notify(response_json.text || `Error deleting email address`, 'error');
            })
            .catch(() => {
              emailAddress.deleting = false;
              this.notyService.notify('Error deleting email address', 'error');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Delete Whitelisted email
   * @param {EmailAddress} emailAddress Whitelisted Email address to delete
   */
  deleteWhitelistedEmail(email: string, index: number) {
    this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-danger';
    this.modalService.confirmDialogComponent.open(`<p>Remove Email - <strong class='text-danger'>${email}</strong> from whitelist ?</p>`).result.then(
      (result) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
        if (result == 'confirmed') {
          this.deletingWhitelist = true;
          this.spamTitanService
            .deleteWhitelist(this.contactId, { value: email })
            .then((response_json) => {
              this.deletingWhitelist = false;
              this.notyService.notify(`Email removed ${email}`, 'warning');
              this.whitelistEmails.splice(index, 1);
            })
            .catch(() => {
              this.deletingWhitelist = false;
              this.notyService.notify('Error removing email from whitelist', 'error');
            });
        }
      },
      (reason) => {
        this.modalService.confirmDialogComponent.confirmButtonClasses = 'btn btn-primary';
      }
    );
  }

  /**
   * Toggle selection of blacklist email
   */
  toggleBlacklistSelection(email: string) {
    if (this.selectedBlacklist.has(email)) {
      this.selectedBlacklist.delete(email);
    } else {
      this.selectedBlacklist.add(email);
    }
  }

  /**
   * Toggle selection of whitelist email
   */
  toggleWhitelistSelection(email: string) {
    if (this.selectedWhitelist.has(email)) {
      this.selectedWhitelist.delete(email);
    } else {
      this.selectedWhitelist.add(email);
    }
  }

  /**
   * Select/deselect all blacklist emails
   */
  toggleAllBlacklist() {
    if (this.selectedBlacklist.size === this.blacklistEmails.length) {
      this.selectedBlacklist.clear();
    } else {
      this.blacklistEmails.forEach(email => this.selectedBlacklist.add(email));
    }
  }

  /**
   * Select/deselect all whitelist emails
   */
  toggleAllWhitelist() {
    if (this.selectedWhitelist.size === this.whitelistEmails.length) {
      this.selectedWhitelist.clear();
    } else {
      this.whitelistEmails.forEach(email => this.selectedWhitelist.add(email));
    }
  }

  /**
   * Bulk delete selected blacklist emails
   */
  bulkDeleteBlacklist() {
    const selected = Array.from(this.selectedBlacklist);
    if (selected.length === 0) return;

    if (!confirm(`Remove ${selected.length} email(s) from blacklist?`)) return;

    this.deletingBlacklist = true;
    this.spamTitanService
      .bulkDeleteBlacklist(this.contactId, selected)
      .then((response_json: any) => {
        this.deletingBlacklist = false;
        this.notyService.notify(response_json?.text || `Removing ${selected.length} email(s) in background...`, 'success');
        // Remove from local list immediately for better UX
        this.blacklistEmails = this.blacklistEmails.filter(e => !this.selectedBlacklist.has(e));
        this.selectedBlacklist.clear();
      })
      .catch(() => {
        this.deletingBlacklist = false;
        this.notyService.notify('Error removing emails from blacklist', 'error');
      });
  }

  /**
   * Bulk delete selected whitelist emails
   */
  bulkDeleteWhitelist() {
    const selected = Array.from(this.selectedWhitelist);
    if (selected.length === 0) return;

    if (!confirm(`Remove ${selected.length} email(s) from whitelist?`)) return;

    this.deletingWhitelist = true;
    this.spamTitanService
      .bulkDeleteWhitelist(this.contactId, selected)
      .then((response_json: any) => {
        this.deletingWhitelist = false;
        this.notyService.notify(response_json?.text || `Removing ${selected.length} email(s) in background...`, 'success');
        // Remove from local list immediately for better UX
        this.whitelistEmails = this.whitelistEmails.filter(e => !this.selectedWhitelist.has(e));
        this.selectedWhitelist.clear();
      })
      .catch(() => {
        this.deletingWhitelist = false;
        this.notyService.notify('Error removing emails from whitelist', 'error');
      });
  }

  /**
   * Form saved
   * @param {boolean} event Indicates if formed saved successfully
   */
  formSaved(event: boolean, emailAddress: EmailAddress | null = null) {
    if (event && !emailAddress) {
      if (!emailAddress) this.loadEmailAddresses();
    } else if (!event && emailAddress) this.loadEmailAddresses();
    if (!emailAddress) this.showNewEmailAddressForm = false;
    else emailAddress.editing = false;
  }

  /**
   * Load Blacklisted Email Addresses
   */
  loadBlacklists() {
    this.loadingBlacklists = true;
    this.spamTitanService
      .indexBlacklist(this.contactId)
      .then((response_json) => {
        this.loadingBlacklists = false;
        this.blacklistEmails = response_json;
      })
      .catch(() => {
        this.loadingBlacklists = false;
      });
  }

  /**
   * Load Email Addresses
   */
  loadEmailAddresses() {
    this.loadingEmailAddresses = true;
    this.emailAddressService
      .index({ contact_id: this.contactId, angular: true })
      .then((response_json) => {
        this.loadingEmailAddresses = false;
        this.emailAddresses = response_json;
      })
      .catch(() => {
        this.loadingEmailAddresses = false;
      });
  }

  /**
   * Load Whitelisted Email Addresses
   */
  loadWhitelists() {
    this.loadingWhitelists = true;
    this.spamTitanService
      .indexWhitelist(this.contactId)
      .then((response_json) => {
        this.loadingWhitelists = false;
        this.whitelistEmails = response_json;
      })
      .catch(() => {
        this.loadingWhitelists = false;
      });
  }

  /**
   * Update positions of email addresses
   */
  updateEmailAddressesPosition() {
    let emailAddressIds: Array<number> = [];
    for (let emailAddress of this.emailAddresses) emailAddressIds.push(emailAddress.id);
    this.emailAddressService
      .updatePositions(this.contactId, emailAddressIds)
      .then((response_json) => {
        if (!response_json.success) this.notyService.notify('Error in sorting email addresses', 'error');
      })
      .catch(() => {
        this.notyService.notify('Error in sorting email addresses', 'error');
      });
  }
}
